/**
 * FaceColoursNode - Adds vertex colors based on face polygon type
 *
 * Takes any geometry and colors faces based on their shape:
 * triangles, squares, pentagons, hexagons, etc.
 */

import type { BufferGeometry } from 'three';
import { BaseNode } from '@nodes/BaseNode';

import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../../threejs/threejsPlugin';
import { addFaceTypeColors } from '../faceTypeColoring';
import { colourPaletteOptions } from '@plugins/video/colorPalettes';
import { internals, params } from '@/nodes/TypedEndpoints';

type ThreeModule = typeof import('three');

const endpoints = {
  ...internals({
    geometryIn: { dataType: 'three:geometry', isInput: true, label: 'Geometry' },
    geometryOut: { dataType: 'three:geometry', isOutput: true }
  }),
  ...params({
    colourPalette: {
      dataType: 'string',
      value: 'primary',
      options: colourPaletteOptions,
      label: 'Palette'
    },
  })


};

class FaceColoursNode extends BaseNode<typeof endpoints> {
  private three: ThreeModule;
  private inputGeometry: BufferGeometry | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  onInternalChanged(payload: any, endpointId: string): void {
    if (endpointId !== 'geometryIn') return;
    this.inputGeometry = payload?.value;
    this.processGeometry();
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (endpointId === 'colourPalette') {
      this.processGeometry();
    }
  }

  private processGeometry(): void {
    if (!this.inputGeometry || !this.three) return;

    // Clone geometry so we don't modify the original
    const geometry = this.inputGeometry.clone();

    // Remove existing colors if any (we'll recompute)
    if (geometry.getAttribute('color')) {
      geometry.deleteAttribute('color');
    }

    // Add face-type colors
    const palette = this.params.colourPalette.value;
    addFaceTypeColors(geometry, this.three, palette);

    this.internals.geometryOut.set(geometry);
  }

  dispose(): void {
    this.inputGeometry = null;
    super.dispose();
  }
}

export const faceColoursNodeDef: NodeDef<FaceColoursNode> = {
  type: 'FaceColours',
  shortName: 'faceColours',
  label: 'Face Colours',
  info: 'Colors faces based on polygon type — triangles, squares, pentagons, hexagons, etc.',
  category: 'modifier',
  endpoints,
  ctor: FaceColoursNode
};
